import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DestroyController } from '@celerofinancas/class-utils';
import { MonitoringService } from '@celerofinancas/service-utils';
import { DeleteUserAction } from '@celerofinancas/state-management';
import { SourceImageDirective } from '@celerofinancas/ui-directives';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { Store } from '@ngxs/store';
import { TokenAuthenticationEvents } from '@shared/models/token-authentication-events';
import { AuthService } from '@shared/services/auth.service';
import { BehaviorSubject, catchError, takeUntil, throwError } from 'rxjs';

/**
 * Component for handling validation messages
 */
@Component({
  selector: 'caixa-webcomponent-validation',
  standalone: true,
  imports: [AsyncPipe, SourceImageDirective],
  templateUrl: './validation.component.html',
  styleUrl: './validation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationComponent extends DestroyController implements OnInit, OnDestroy {
  /**
   * Messages to be displayed during the validation process
   */
  public readonly messages: string[] = [
    'Aguarde! Estamos organizando suas finanças em um só lugar.',
    'Preparando seu espaço para decisões financeiras inteligentes.',
    'Conectando você ao controle total do seu dinheiro.',
    'Só mais um pouco. Queremos que você aproveite cada benefício.',
    'Prontos para dar a visão total que o seu negócio precisa!',
  ];

  /**
   * Current message index
   */
  private currentMessageIndex = 0;

  /**
   * Current message
   */
  public currentMessage$ = new BehaviorSubject(this.messages[0]);

  /**
   * interval id for the message rotation
   */
  private intervalId?: ReturnType<typeof setInterval>;

  /**
   * Monitoring service reference
   */
  private readonly monitoring = new MonitoringService();

  /**
   * Auth service
   */
  private readonly auth = inject(AuthService);

  /**
   * Token handler service
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Store management
   */
  private readonly store = inject(Store);

  /**
   * Current route
   */
  private readonly route = inject(ActivatedRoute);

  /**
   * Router service
   */
  private readonly router = inject(Router);

  /**
   * Loading state signal
   */
  public loading = signal(true);

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.rotationMessageHandler();
    this.handleTokenAuthentication();
  }

  /**
   * Destroys the component
   */
  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    clearInterval(this.intervalId);
  }

  /**
   * Handles the rotation of messages
   * This method sets an interval to change the current message every 10 seconds
   */
  private rotationMessageHandler(): void {
    this.intervalId = setInterval(() => {
      this.currentMessageIndex = (this.currentMessageIndex + 1) % this.messages.length;
      this.currentMessage$.next(this.messages[this.currentMessageIndex]);
    }, 10000);
  }

  /**
   * Handles token exchange errors
   */
  private handleErrorToken(): void {
    this.tokens.eventsSubject$.next({
      event: TokenAuthenticationEvents.TOKEN_EXCHANGED_FAILED,
    });
    this.tokens.clearTokensFromStorage();
    this.store.dispatch(new DeleteUserAction());
    this.loading.set(false);
    clearInterval(this.intervalId);
    this.currentMessage$.next(
      '<span class="validation-error-title">Tivemos um problema com o seu acesso</span><br /> Não foi possível realizar sua autenticação no Meu CAIXA. Por favor, atualize a página e tente novamente. <br /> Se o erro persistir, entre em contato com o suporte da ferramenta pelo e-mail <b>atendimento.meucaixa@celero.com.br</b>',
    );
  }

  /**
   * Handles the token exchange process
   */
  private handleTokenAuthentication(): void {
    this.route.queryParams.pipe(takeUntil(this.destroyController$)).subscribe((params) => {
      if (
        params?.idToken &&
        (!this.tokens.accessToken || (this.tokens.accessToken && !this.tokens.isRefreshTokenValid))
      ) {
        this.auth
          .tokenAuthentication(params?.idToken)
          .pipe(
            catchError((error) => {
              this.handleErrorToken();
              return throwError(() => error);
            }),
            takeUntil(this.destroyController$),
          )
          .subscribe(({ body: response }) => {
            this.tokens.storeTokens(response);

            const backUrl = this.normalizeBackUrl(params?.backUrl);

            if (backUrl) {
              this.router.navigate([`/${backUrl}`]);
            }
          });
      } else {
        this.handleErrorToken();
      }
    });
  }

  /**
   * Normalizes the return route from query params.
   * Supports legacy formats like repeated params (array) or comma-separated values.
   *
   * @param backUrlParam The raw backUrl parameter from query params
   */
  private normalizeBackUrl(backUrlParam?: string | string[]): string | null {
    if (!backUrlParam) {
      return null;
    }

    const rawBackUrl = Array.isArray(backUrlParam) ? backUrlParam.join('/') : backUrlParam;

    return rawBackUrl.replace(/,/g, '/').replace(/^\/+|\/+$/g, '');
  }
}
